import React, { useState } from 'react';
import { X, Plus, DollarSign, User, Clock, Trash2, Check } from 'lucide-react';

export interface ExpenseData {
  id: string;
  title: string;
  amount: string;
  category: string;
  createdOn: number;
  createdUserId: string;
  createdUserName: string;
  deleted: string;
  inventoryId: string;
  note: string;
  slotEndTime: string;
  slotId: string;
  slotStartTime: string;
  status: number;
  transactionMode: string;
  tripTitle: string;
  type: string;
  displayTime: string;
  againstUserId?: string;
  againstUserName?: string;
}

export interface EarningData {
  tripCount: number;
  bookingCount: number;
  amountReceived: number;
  amountUsed: number;
  profit: number;
}

interface ExpenseDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  slotId: string;
  slotExpenseData: ExpenseData[];
  slotEarningData?: EarningData;
  onAddExpense: () => void;
  onDeleteExpense: (expenseId: string) => void;
  onClose: () => void;
}

const ExpenseListItem: React.FC<{
  expense: ExpenseData;
  onDelete: (expenseId: string) => void;
}> = ({ expense, onDelete }) => {
  const amount = expense?.amount ? parseInt(expense.amount) / 100 : 0;
  const { title, type, createdUserName, transactionMode, note, createdOn, status, againstUserId = 0, againstUserName = "" } = expense;
  const expenseApproved = status === 1;

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const day = date.getDate();
    const month = date.toLocaleString('en-IN', { month: 'short' });

    const amPm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;

    return `${formattedHours}:${minutes} ${amPm} ${day} ${month}`;
  };

  return (
    <div className={`p-4 border rounded-lg mb-2 transition-colors ${
      expenseApproved 
        ? 'bg-green-50 border-green-200' 
        : 'bg-white border-gray-200 hover:bg-gray-50'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="font-medium text-gray-900 mb-1">{title}</div>
          
          <div className="text-sm text-gray-500 mb-1">
            {type}: {againstUserId && againstUserName ? (
              <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                {againstUserName}
              </span>
            ) : (
              <span className="flex items-center space-x-1">
                <Clock className="h-3 w-3" />
                <span>{formatTimestamp(createdOn)}</span>
              </span>
            )}
          </div>

          {againstUserId && (
            <div className="text-sm text-gray-500 mb-1">
              <span className="flex items-center space-x-1">
                <Clock className="h-3 w-3" />
                <span>{formatTimestamp(createdOn)}</span>
              </span>
            </div>
          )}

          {note && (
            <div className="text-sm text-gray-500 max-w-[280px]">
              {note}
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
            {createdUserName}
          </span>
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
            {transactionMode}
          </span>
          <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-medium">
            ₹{amount}
          </span>
          <button
            onClick={() => onDelete(expense.id)}
            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {expenseApproved && (
            <div className="flex items-center text-green-600">
              <Check className="h-5 w-5" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const sumAmountBySelfTransaction = (expense: ExpenseData[]) => {
  const userAmountMap: any = {};
  let totalTripExpense = 0;
  for (const entry of expense) {
    const amount = parseInt(entry.amount) / 100;
    if (entry.transactionMode === "SELF") {
      const userId = entry.createdUserId;
      const userName = entry.createdUserName;
      if (!userAmountMap[userId]) {
        userAmountMap[userId] = { userId, userName, expense: 0 };
      }
      userAmountMap[userId].expense += amount;
    }
    totalTripExpense += amount;
  }
  const userSummary = Object.values(userAmountMap);
  userSummary.push({ id: 1, userName: "TRIP EXPENSE", "expense": totalTripExpense });
  return Object.values(userSummary);
};

const tripEarningArray = (earningData: EarningData) => {
  const earningArray = [];
  if (earningData) {
    if (earningData?.amountReceived) {
      earningArray.push({ id: 11, userName: "RECEIVED", "expense": earningData.amountReceived });
    }
    if (earningData?.amountUsed) {
      earningArray.push({ id: 12, userName: "USED", "expense": earningData.amountUsed });
    }
    if (earningData?.profit) {
      earningArray.push({ id: 13, userName: "PROFIT", "expense": earningData.profit });
    }
  }
  return Object.values(earningArray);
};

const ExpenseDialog: React.FC<ExpenseDialogProps> = ({
  open,
  setOpen,
  slotId,
  slotExpenseData,
  slotEarningData,
  onAddExpense,
  onDeleteExpense,
  onClose
}) => {
  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleAddExpense = () => {
    onAddExpense();
    setOpen(false);
  };

  const expenseSummationData = sumAmountBySelfTransaction(slotExpenseData);
  const tripEarningData = slotEarningData ? tripEarningArray(slotEarningData) : [];

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-900">Expenses</h3>
            <button
              onClick={handleAddExpense}
              className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
              title="Add Expense"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-96 overflow-y-auto">
          {/* Trip Earnings Summary */}
          {tripEarningData?.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Trip Earnings</h4>
              <div className="flex flex-wrap gap-2">
                {tripEarningData.map((earningData: any) => (
                  <span key={earningData?.id} className="text-sm text-gray-600">
                    {earningData.userName}: ₹{earningData.expense}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Expense Summary */}
          {expenseSummationData.length > 0 && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Expense Summary</h4>
              <div className="flex flex-wrap gap-2">
                {expenseSummationData.map((expenseData: any) => (
                  <span key={expenseData?.userId} className="text-sm text-gray-600">
                    {expenseData.userName}: ₹{expenseData.expense}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Expenses List */}
          {slotExpenseData?.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Expenses Submitted</h4>
              <p className="text-gray-600 mb-4">No expenses have been added for this trip.</p>
              <button
                onClick={handleAddExpense}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Add First Expense
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {slotExpenseData.map((expense: ExpenseData) => (
                <ExpenseListItem
                  key={expense?.id}
                  expense={expense}
                  onDelete={onDeleteExpense}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpenseDialog;
